import React, { FC, ReactElement, ReactNode, useCallback, useMemo, useState } from "react";
import { Platform, Pressable } from "react-native";
import { useDevice, View } from "@lookiero/aurora";
import { OS } from "../../shared/config";
import { style as tooltipStyle } from "./Tooltip.style";

enum Placement {
  TOP = "top",
  BOTTOM = "bottom",
}

interface TooltipProps {
  readonly visible?: boolean;
  readonly content: ReactNode;
  readonly placement?: Placement;
  readonly children: (visible: boolean) => ReactElement;
}

const Tooltip: FC<TooltipProps> = ({ children, content, placement = Placement.BOTTOM, visible = false }) => {
  const [isVisible, setIsVisible] = useState(visible);
  const { screen } = useDevice();
  const isLargeWeb = useMemo(() => {
    return screen.L && Platform.OS === OS.WEB;
  }, [screen.L]);

  const style = useMemo(() => tooltipStyle(), []);

  const toggleVisible = useCallback(() => {
    setIsVisible((previousState) => {
      return !previousState;
    });
  }, []);

  const events = useMemo(() => {
    if (isLargeWeb) {
      return {
        onHoverIn: toggleVisible,
        onHoverOut: toggleVisible,
      };
    }
    return {
      onPress: () => {
        toggleVisible();
        setTimeout(() => {
          toggleVisible();
        }, 1000);
      },
    };
  }, [isLargeWeb, toggleVisible]);

  return (
    <View style={style.container}>
      <Pressable {...events} pointerEvents={isVisible && !isLargeWeb ? "none" : undefined}>
        {children(isVisible)}
      </Pressable>
      {isVisible ? (
        <View style={[style.wrapper, placement === Placement.TOP ? style.wrapperTop : style.wrapperBottom]}>
          <View style={[style.arrow, placement === Placement.TOP ? style.arrowTop : style.arrowBottom]} />
          <View style={[style.content, placement === Placement.TOP ? style.contentTop : style.contentBottom]}>
            <View style={style.contentWrapper}>{content}</View>
          </View>
        </View>
      ) : null}
    </View>
  );
};

export { Tooltip, Placement };
